import { useEffect, useState } from 'react';
import { ArrowUp } from 'lucide-react';
import { useLang } from '@/lib/LanguageContext';

/**
 * BackToTop — bouton flottant bas-GAUCHE qui apparait apres 800px de scroll.
 * Position gauche pour ne pas chevaucher le WhatsApp FAB a droite.
 * Theme sage Oceanne coherent. Cache jusqu'a md (768px+) pour eviter
 * chevauchement avec MobileStickyBar qui occupe le bottom mobile.
 * Smooth-scroll vers le haut, respecte prefers-reduced-motion.
 * Halo sage pulsant 2-layer parite cross-Intralys.
 */
export function BackToTop() {
  const { lang } = useLang();
  // Phase 27.72D — lazy init (avant : setState() dans useEffect body = cascade render).
  const [visible, setVisible] = useState(
    () => typeof window !== 'undefined' && window.scrollY > 800
  );

  useEffect(() => {
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        setVisible(window.scrollY > 800);
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  function scrollToTop() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, left: 0, behavior: reduced ? 'auto' : 'smooth' });
  }

  const label = lang === 'fr' ? 'Retour en haut' : 'Back to top';

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label={label}
      className={`group hidden md:flex fixed bottom-6 left-6 z-40 w-12 h-12 items-center justify-center rounded-full bg-background border border-secondary/35 text-secondary hover:border-secondary/70 hover:bg-secondary/10 hover:ring-2 hover:ring-rose-blush/40 hover:-translate-y-0.5 active:scale-95 transition-all duration-300 shadow-lg ${
        visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}
    >
      {/* Tooltip hover desktop a droite du bouton */}
      <span className="pointer-events-none absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-medium whitespace-nowrap opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        {label}
      </span>

      {/* Halo sage pulsant 2-layer (parite WhatsApp pill) */}
      <span
        className="absolute inset-0 rounded-full bg-secondary/30 motion-safe:animate-ping pointer-events-none group-hover:hidden"
        style={{ animationDuration: '2.4s' }}
        aria-hidden
      />
      <span
        className="absolute -inset-1 rounded-full bg-secondary/15 motion-safe:animate-ping pointer-events-none group-hover:hidden"
        style={{ animationDuration: '2.4s', animationDelay: '0.6s' }}
        aria-hidden
      />

      <ArrowUp className="relative w-4 h-4" strokeWidth={2} aria-hidden />
    </button>
  );
}
